import React from "react";
import { Transaction } from "../../../domain/types/Transaction";
import { formatCurrency, getTextColor } from "../utils/transactionFormatters";
import { TransactionActions } from "./TransactionActions";
import { BankAccount } from "../../../domain/types/banks/BankAccount";
import { Bank } from "../../../domain/types/banks/Bank";
import Tooltip from "../../common/Tooltip";
import {
  HiOutlineArrowUp,
  HiOutlineArrowDown,
  HiOutlineTag,
  HiOutlineCalendar,
} from "react-icons/hi";
import { HiOutlineBanknotes } from "react-icons/hi2";

interface TransactionCardProps {
  transaction: Transaction;
  account: BankAccount | null;
  bank: Bank | undefined;
  isSelected: boolean;
  onSelect: (transaction: Transaction, selected: boolean) => void;
  onClick: () => void;
}

export const TransactionCard: React.FC<TransactionCardProps> = ({
  transaction,
  account,
  bank,
  isSelected,
  onSelect,
  onClick,
}) => {
  return (
    <div
      className="group flex items-stretch bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100 dark:border-gray-700 overflow-hidden"
      onClick={onClick}
    >
      {/* Selection Column */}
      <div className="flex items-center px-4 border-r border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(transaction, e.target.checked)}
          className="form-checkbox h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {/* Main Content */}
      <div className="flex-grow flex items-center justify-between p-4">
        {/* Left Section: Transaction Info */}
        <div className="flex items-center space-x-4">
          {/* Transaction Type Icon */}
          <div
            className={`flex-shrink-0 p-2 rounded-full ${
              transaction?.transaction_type?.toLowerCase() === "income"
                ? "bg-green-100 dark:bg-green-900"
                : "bg-red-100 dark:bg-red-900"
            }`}
          >
            {transaction?.transaction_type?.toLowerCase() === "income" ? (
              <HiOutlineArrowDown className="w-6 h-6 text-green-600 dark:text-green-400" />
            ) : (
              <HiOutlineArrowUp className="w-6 h-6 text-red-600 dark:text-red-400" />
            )}
          </div>

          {/* Transaction Details */}
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {transaction.merchant_name ||
                  transaction.transaction_details?.name ||
                  transaction.category_description}
              </span>
              <span
                className={`text-lg font-bold ${getTextColor(transaction.transaction_type)}`}
              >
                {formatCurrency(transaction.amount)}
              </span>
            </div>

            {/* Category and Date */}
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center text-gray-600 dark:text-gray-400">
                <HiOutlineTag className="w-4 h-4 mr-1" />
                {transaction.category_description}
              </div>
              <div className="flex items-center text-gray-600 dark:text-gray-400">
                <HiOutlineCalendar className="w-4 h-4 mr-1" />
                {new Date(transaction.transaction_date).toLocaleDateString()}
              </div>
            </div>

            {/* Bank Account Info */}
            {account && (
              <Tooltip content={renderAccountDetails(account, bank)}>
                <div className="flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200">
                  <HiOutlineBanknotes className="w-4 h-4 mr-1" />
                  <span>
                    {bank?.name || "Unknown Bank"} - ****{account.mask}
                  </span>
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Right Section: Actions */}
        <div className="flex items-center space-x-4">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <TransactionActions transaction={transaction} />
          </div>
          <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600">
            <HiOutlineArrowDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to render account details in the tooltip
function renderAccountDetails(account: BankAccount, bank?: Bank) {
  return (
    <div className="max-w-sm p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg text-left">
      {/* Bank Logo and Name */}
      <div className="flex items-center space-x-4 mb-4">
        {bank?.logo ? (
          <img
            src={`data:image/png;base64, ${bank.logo}`}
            alt={`${bank.name} Logo`}
            className="w-12 h-12 rounded-full border"
          />
        ) : (
          <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <HiOutlineBanknotes className="text-gray-400 w-6 h-6" />
          </div>
        )}
        <div>
          <div className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            {bank?.name || "Unknown Bank"}
          </div>
          {bank?.url && (
            <a
              href={bank.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800"
            >
              Visit Bank Website
            </a>
          )}
        </div>
      </div>
      {/* Account Details */}
      <div className="text-sm space-y-2">
        <div>
          <span className="font-medium text-gray-600 dark:text-gray-400">
            Account Name:{" "}
          </span>
          <span>{account.name}</span>
        </div>
        <div>
          <span className="font-medium text-gray-600 dark:text-gray-400">
            Type:{" "}
          </span>
          <span>{account.type}</span>
        </div>
        {account.subType && (
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-400">
              Sub-Type:{" "}
            </span>
            <span>{account.subType}</span>
          </div>
        )}
        {account.mask && (
          <div>
            <span className="font-medium text-gray-600 dark:text-gray-400">
              Account Number:{" "}
            </span>
            <span>**** **** **** {account.mask}</span>
          </div>
        )}
      </div>
    </div>
  );
}
